export class ApiError extends Error {
  constructor(public status: number, public payload: { error?: string; message?: string }) {
    super(payload.message || payload.error || `HTTP ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let payload: { error?: string; message?: string } = {};
    try {
      payload = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  // Raw-body POST for a file upload; same error handling as the JSON calls.
  upload: <T>(path: string, body: Blob, contentType = 'application/zip') =>
    request<T>(path, { method: 'POST', body, headers: { 'Content-Type': contentType } }),
};

export interface Operator {
  id: string;
  email: string;
  name: string | null;
  last_login: string | null;
  active?: boolean;
  has_password?: boolean;
  password_updated_at?: string | null;
  created_at?: string;
  created_by?: string | null;
}

export interface ServerProcessEntry {
  pid: number;
  user: string;
  cpu_percent: number;
  mem_percent: number;
  rss_kb: number;
  command: string;
}

export interface ServerDiskEntry {
  filesystem: string;
  mount: string;
  total_bytes: number;
  used_bytes: number;
  available_bytes: number;
  use_percent: number;
}

export interface ServerChromeStatus {
  docker_ok: boolean;
  present: boolean;
  running: boolean;
  cdp: 'ok' | 'fail' | 'skip';
  targets: number | null;
}

export interface ServerMetrics {
  collected_at: string;
  load: { '1m': number; '5m': number; '15m': number } | null;
  cpu_count: number | null;
  uptime_seconds: number | null;
  memory: {
    total_kb: number;
    available_kb: number;
    used_kb: number;
    free_kb: number;
    buffers_kb: number;
    cached_kb: number;
  } | null;
  swap: { total_kb: number; used_kb: number; free_kb: number } | null;
  disks: ServerDiskEntry[];
  top_cpu: ServerProcessEntry[];
  top_mem: ServerProcessEntry[];
  os: string | null;
  kernel: string | null;
  chrome: ServerChromeStatus | null;
  error?: string;
}

export interface Server {
  id: string;
  region: string;
  hostname: string;
  ssh_user: string;
  ssh_key_id: string | null;
  capacity: number;
  // The address this host leaves by. Only a cross-server website move needs
  // it, and it learns it from the host itself the first time — set it by hand
  // only for a host behind NAT.
  public_ip: string | null;
  status: 'active' | 'draining' | 'decommissioned';
  health_status: 'healthy' | 'unhealthy' | 'unknown' | null;
  last_health_at: string | null;
  health_detail: { container_count?: number; running?: number; error?: string | null } | null;
  metrics_detail: ServerMetrics | null;
  last_metrics_at: string | null;
  notes: string | null;
  created_at: string;
  tenant_count?: number;
}

export interface Tenant {
  id: string;
  server_id: string;
  customer_email: string | null;
  customer_name: string | null;
  company_name: string | null;
  legacy_hostnames: string[];
  status: 'provisioning' | 'awaiting_auth' | 'active' | 'suspended' | 'error' | 'deleted';
  // How the tenant was suspended (null = not suspended; null while suspended =
  // legacy hard suspend). 'payment_required' = soft: container running, chat
  // blocked with the payment message. Resume auto-resolves from this.
  suspend_kind: 'hard' | 'payment_required' | null;
  claude_oauth_status: 'none' | 'authorized' | 'expired';
  claude_oauth_authorized_at: string | null;
  claude_oauth_email: string | null;
  claude_oauth_plan: string | null;
  codex_oauth_status: 'none' | 'authorized' | 'expired';
  codex_oauth_authorized_at: string | null;
  codex_oauth_email: string | null;
  codex_oauth_plan: string | null;
  anthropic_api_key_set: boolean;
  anthropic_api_key_set_at: string | null;
  openai_api_key_set: boolean;
  openai_api_key_set_at: string | null;
  container_status: 'running' | 'stopped' | 'missing' | null;
  container_started_at: string | null;
  health_status: 'healthy' | 'unhealthy' | 'unknown' | null;
  last_health_at: string | null;
  last_chat_activity_at: string | null;
  // Last successful activity read by the health poll (null = never read).
  last_chat_activity_checked_at: string | null;
  // Registered WordPress sites, counted from sites.json on the host. null =
  // unknown (no data dir there); a tenant that never registered one is 0.
  site_count: number | null;
  // Grace-period removal mark (Cleanup page). A due date in the future
  // protects the tenant from every sweep.
  never_delete: boolean;
  removal_marked_at: string | null;
  removal_due_at: string | null;
  removal_context: RemovalContext | null;
  // Host-side credentials-file sweep — what the list's auth tags read: has this
  // tenant ever connected a provider? null = never swept. The *_oauth_status
  // fields above are the live CLI check's record, refreshed only when an
  // operator presses the probe button on the detail page.
  claude_creds_present: boolean | null;
  codex_creds_present: boolean | null;
  auth_files_checked_at: string | null;
  // When the Claude login ends, from the same sweep (about 4 weeks after sign-in;
  // renewing doesn't move it). null = not known, or an API key is in use.
  claude_login_expires_at: string | null;
  override_count: number;
  tier: string | null;
  notes: string | null;
  created_at: string;
  deleted_at: string | null;
}

// ── Account sweeper (Cleanup page) ─────────────────────────────────────────

export interface SweepRule {
  name: string;
  last_used_days: number;
  registered_before_days: number | null;
  tiers: string[];
  no_sites: boolean;
  one_site: boolean;
  no_ai_auth: boolean;
}

export interface RemovalContext {
  // Matched rule name, or 'manual' (marked by hand on the tenant page).
  rule: string;
  trigger: 'sweeper_auto' | 'sweeper_manual' | 'operator';
  run_id: string | null;
  last_used_at: string | null;
  site_count: number | null;
  ai_authenticated: boolean | null;
  operator_email: string | null;
  criteria: Omit<SweepRule, 'name'> | null;
}

export interface SweeperConfig {
  enabled: boolean;
  interval_hours: number;
  rules: SweepRule[];
  grace_days: number;
  max_removals_per_run: number;
  marked_webhook_url: string;
  removed_webhook_url: string;
}

export interface WebhookFailure {
  at: string;
  event: string | null;
  tenant_id: string | null;
  http_status: number | null;
  error: string | null;
}

export interface SweeperConfigResponse {
  config: SweeperConfig;
  // 'unreadable' = stored under a different CP_MASTER_KEY; re-enter it.
  webhook_secret: 'none' | 'ok' | 'unreadable';
  state: { last_run_at: string | null; last_run_id: string | null; next_run_at: string | null };
  last_webhook_failure: { marked: WebhookFailure | null; removed: WebhookFailure | null };
}

export type SweepSkipReason =
  | 'activity_never_read'
  | 'activity_stale'
  | 'ai_auth_unknown'
  | 'site_count_unknown'
  | 'protected'
  | 'never_delete'
  | 'check_failed';

export interface SweepCandidate {
  tenant_id: string;
  server_id: string;
  customer_email: string | null;
  customer_name: string | null;
  company_name: string | null;
  tier: string | null;
  status: string;
  created_at: string | null;
  last_used_at: string;
  idle_days: number;
  site_count: number | null;
  ai_authenticated: boolean;
  rule: string | null;
  checked: 'live' | 'db_only' | 'not_needed';
  marked_at: string | null;
  due_at: string | null;
  reason?: SweepSkipReason;
}

export interface SweepPreview {
  run_id: string;
  candidates: SweepCandidate[];
  skipped: SweepCandidate[];
}

export type SweepRunKind = 'manual_remove' | 'manual_mark' | 'auto';
export type SweepRunTrigger = 'auto' | 'run_now' | 'manual';

export interface SweepRunSummary {
  run_id: string;
  kind: SweepRunKind;
  trigger: SweepRunTrigger;
  started_at: string;
  finished_at: string | null;
  candidates: number;
  unknown_skipped: number;
  protected_skipped: number;
  marked: number;
  removed: number;
  unmarked: number;
  failed: number;
  deferred: number;
  no_longer_matching: number;
  error: string | null;
}

export interface SweepStatus {
  running: {
    run_id: string;
    kind: SweepRunKind;
    trigger: SweepRunTrigger;
    started_at: string;
    phase: 'checking' | 'marking' | 'removing';
    total: number;
    done: number;
  } | null;
  last: SweepRunSummary | null;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
}

// One `sweeper.run` activity row.
export interface SweepRunRow {
  id: string;
  created_at: string;
  run_id: string;
  outcome: 'success' | 'error';
  error_message: string | null;
  operator_email: string | null;
  parameters: {
    trigger?: SweepRunTrigger;
    dry_run?: boolean;
    action?: 'remove' | 'mark' | 'auto';
    rules?: SweepRule[];
    candidates?: number;
    unknown_skipped?: number;
    protected_skipped?: number;
    marked?: number;
    removed?: number;
    unmarked?: number;
    failed?: number;
    deferred?: number;
    no_longer_matching?: number;
    candidate_list?: Array<{
      tenant_id: string;
      customer_email: string | null;
      company_name: string | null;
      tier: string | null;
      status: string;
      last_used_at: string;
      site_count: number | null;
      ai_authenticated: boolean;
      rule: string | null;
      checked: string;
    }>;
    candidate_list_truncated?: boolean;
  } | null;
}

export interface MarkedTenant {
  id: string;
  server_id: string;
  status: string;
  tier: string | null;
  customer_email: string | null;
  company_name: string | null;
  created_at: string;
  last_used_at: string;
  site_count: number | null;
  ai_authenticated: boolean;
  removal_marked_at: string;
  removal_due_at: string;
  removal_context: RemovalContext | null;
}

export type RemovalLogKind = 'removed' | 'marked' | 'unmarked' | 'postponed';
export type RemovalTrigger = 'sweeper_auto' | 'sweeper_manual' | 'operator' | 'external_api';

export interface RemovalLogEntry {
  id: string;
  created_at: string;
  kind: RemovalLogKind;
  outcome: 'success' | 'error';
  error_message: string | null;
  trigger: RemovalTrigger;
  run_id: string | null;
  rule: string | null;
  operator_email: string | null;
  tenant_id: string;
  server_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  company_name: string | null;
  tier: string | null;
  tenant_status: string | null;
  tenant_created_at: string | null;
  last_used_at: string | null;
  site_count: number | null;
  ai_authenticated: boolean | null;
  marked_at: string | null;
  due_at: string | null;
  mode: string | null;
  webhook_status: 'pending' | 'delivered' | 'failed' | 'not_configured' | 'skipped' | null;
  detail: Record<string, unknown> | null;
}

export interface Tier {
  id: string;
  label: string;
  description: string | null;
  features: Record<string, boolean>;
  is_default: boolean;
  sort_order: number;
  default_activations_limit: number;
  default_users_limit: number;
  // Markdown; non-empty → tenants on this tier get this text back instead of a
  // chat turn (and scheduled runs are refused). Empty = chat allowed.
  chat_blocked_message: string;
  // Usage limit. Entitlements — a save re-pushes them to every live tenant on
  // the tier, unlike the default_* seeds above.
  //
  // Which limit applies: 'none' = no usage limits, 'usd' = meter the turn's API
  // cost in dollars, 'runtime' = meter the turn's working time in minutes.
  credit_mode: 'none' | 'usd' | 'runtime';
  // Per period, IN credit_mode'S UNIT — dollars or minutes.
  // pg returns NUMERIC as a string over the wire; coerce before arithmetic.
  credit_allowance: number | string;
  // 0 = one-time grant that never renews. 24 daily / 168 weekly / 720 monthly.
  credit_period_hours: number;
  // Restart the allowance (balance + period) once, when the tenant connects its
  // first AI provider.
  credit_reset_on_first_connect: boolean;
  // What the allowance is advertised to buy, in user-facing "tasks". Display
  // only. 0 = unset → the plugin shows a percentage instead.
  allowance_tasks: number;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  tenant_count: number;
}

// One entry of the hardcoded feature registry (control-plane/src/lib/features.ts),
// returned alongside the tiers so the editor can render a checkbox per feature.
export interface FeatureDef {
  key: string;
  label: string;
  description: string;
}

export interface SshKey {
  id: string;
  label: string;
  public_fingerprint: string;
  created_at: string;
}

export interface ActivityEntry {
  id: number;
  operator_id: string | null;
  operator_email?: string | null;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  parameters: Record<string, unknown> | null;
  outcome: 'success' | 'error';
  error_message: string | null;
  created_at: string;
}

export interface DashboardSummary {
  servers_by_status: Record<string, number>;
  tenants_by_status: Record<string, number>;
  capacity_used: number;
  capacity_total: number;
  recent_activity: ActivityEntry[];
}

export interface CpSettingField {
  key: string;
  label: string;
  description: string;
  type: 'json' | 'string' | 'boolean' | 'number';
  kind: 'shared' | 'seed' | 'locked' | 'cp_only';
  // Optional suffix rendered next to numeric/text inputs ('%', 'minutes') and
  // placeholder for fields that default to blank.
  unit?: string;
  placeholder?: string;
  cp_global: unknown;
  cp_global_stored: boolean;
  default_value: unknown;
}

export interface TenantSettingField extends CpSettingField {
  tenant_override?: unknown;
  // The tenant's live value (read from its orchestrator's settings.json),
  // falling back to a CP override or the CP-global default when unreachable.
  effective: unknown;
  // 'tenant' = live value diverges from the CP-global default (tenant changed
  // it in their dashboard); 'override' = CP-side per-tenant override; else
  // 'inherited'.
  source: 'inherited' | 'override' | 'tenant';
  // false for CP-global fields that tenants manage in their own dashboard
  // (modes, defaultModeId, modelSwitch.enabled) — shown read-only here.
  tenant_overridable: boolean;
}

export interface Skill {
  name: string;
  description: string;
  auto_deploy: boolean;
  file_count: number;
}

export interface SkillsCatalog {
  source_dir: string;
  source_present: boolean;
  skills: Skill[];
  auto_deploy_list: string[];
  error?: string;
}

export interface DeployResult {
  attempted: number;
  ok: number;
  failed: number;
  deployments_ok: number;
  deployments_failed: number;
  catalog_refreshed: boolean;
  catalog_error?: string;
  results: Array<{
    server_id: string;
    ok: boolean;
    git_pulled: boolean;
    git_error?: string;
    error?: string;
    deployments: Array<{
      container: string;
      ok: boolean;
      error?: string;
    }>;
  }>;
}

export interface PropagateResult {
  attempted: number;
  ok: number;
  failed: number;
  // Tenants left untouched by the no-force customization guard: their live
  // value diverged from the last CP-delivered value (changed in their own
  // dashboard), or their live settings couldn't be read to tell.
  skipped: Array<{ tenant_id: string; reason: 'customized' | 'live_unreadable' }>;
  overrides_cleared: number;
  force: boolean;
  results: Array<{
    tenant_id: string;
    ok: boolean;
    status: number;
    reason?: string;
    override_cleared?: boolean;
  }>;
  endpoint_built: boolean;
}

// ── Background host jobs (Tools) ───────────────────────────────────────────
// Work the control plane drives over SSH that outlives a request: today, moving
// a website between tenants. Polled from GET /api/tools/jobs/:id.

export interface HostJob {
  id: string;
  kind: 'move_site';
  status: 'running' | 'succeeded' | 'failed' | 'interrupted';
  phase: string;
  site_id: string | null;
  from_tenant_id: string | null;
  to_tenant_id: string | null;
  from_server_id: string | null;
  to_server_id: string | null;
  params: { remove_source_data?: boolean; copy_shared_rules?: boolean; cross_server?: boolean };
  progress: { pass?: number; bytes_total?: number; bytes_done?: number; pct?: number };
  result: MoveSiteResult | null;
  warnings: string[];
  // What a finished job still has to undo on the hosts (a container to start,
  // a transfer key or staged copy to remove). Retried by the control plane
  // every few minutes; the job's tenants can't be moved again until it's empty.
  pending_recovery: string[];
  error_message: string | null;
  log_tail: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface MoveSiteResult {
  site_id: string;
  from: string;
  to: string;
  cross_server: boolean;
  site_bytes: number;
  claude_sessions: number;
  codex_rollouts: number;
  purged_source: boolean;
  trash_entry: string;
  // Older copies of this site's transcripts that were already on the target
  // and were replaced by the incoming ones; kept in its trash at replaced_entry.
  replaced_transcripts: number;
  replaced_entry: string;
  restart_failed: string[];
  source_released: boolean;
  // null = the shared rules weren't asked for. Optional: absent on moves made
  // before the option existed.
  shared_rules?: SharedRulesCopy | null;
}

// What copying the old tenant's shared ("All my websites") rules did.
export interface SharedRulesCopy {
  added: string[];
  // exists = already on the new tenant (copied before), name_taken = a different
  // rule has that name there, invalid = the new tenant can't store it as it is.
  skipped: { name: string; reason: 'exists' | 'name_taken' | 'invalid'; message?: string }[];
  // Switched off for this website on the old tenant, so left behind.
  switched_off: number;
}

// Custom skills installed for every website of a tenant (tenant page → Skills).
export type TenantSkillSiteStatus =
  | 'installed'
  | 'local_edits'
  | 'forked'
  | 'own_skill'
  | 'opted_out'
  | 'pending';

export interface TenantSkill {
  slug: string;
  name: string;
  description: string;
  fileCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  replacesBuiltin: boolean;
  sites: { id: string; url: string | null; status: TenantSkillSiteStatus }[];
}

export interface TenantSkillDeploy {
  refreshed: number;
  added: number;
  pruned: number;
  skipped: number;
  adopted: number;
  failed: { siteId: string; slug: string; error: string }[];
}
